import BasePeripheral from '@appblocks/codegeneration/Peripherals/_BasePeripheral';
import {
    SettingType, Setting, BlockType, ValidationMessage, ValidationMessageType,
    TibboDataType,
    SettingControlType,
    CodeBlock,
    SettingState,
} from '@appblocks/codegeneration/types';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';
import Peripherals from '@appblocks/codegeneration/Peripherals';
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import * as micropython from './micropython';
import * as zephyr from './zephyr';
import BaseDisplayPeripheral from '../_BaseDisplayPeripheral';

const LCD_SCREEN_WIDGET_COLUMNS = 128;
const LCD_SCREEN_WIDGET_ROWS = 64;

export default class ST7789 extends BaseDisplayPeripheral {
    constructor() {
        super();
        this.id = 'st7789';
        this.name = 'ST7789 Display Controller';

        this.requirements = [['zephyr', 'micropython']];

        const settings: Array<Setting> = [
            {
                id: 'PERIPHERAL',
                name: 'Peripheral',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'MODE',
                name: 'Mode',
                settingType: SettingType.OPTION,
                group: 'GENERAL',
                default: '',
                options: [
                    {
                        name: 'SPI',
                        value: 'spi',
                    },
                    {
                        name: 'Parallel Interface 8080',
                        value: 'parallel',
                    },
                ],
            },
            {
                id: 'SPI',
                name: 'SPI Peripheral',
                settingType: SettingType.OPTION,
                refId: 'spi',
                group: 'GENERAL',
                default: '',
            },
            {
                id: 'CS',
                name: 'Chip Select Pin',
                settingType: SettingType.OPTION,
                required: true,
                refId: 'gpio',
                group: 'PERIPHERAL',
                default: '',
            },
            {
                id: 'ADDRESS',
                name: 'Address',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'PERIPHERAL',
                default: 0,
            },
            {
                id: 'D0',
                name: 'D0',
                settingType: SettingType.OPTION,
                required: true,
                group: 'PERIPHERAL',
                refId: 'gpio',
                default: '',
            },
            {
                id: 'D1',
                name: 'D1',
                settingType: SettingType.OPTION,
                required: true,
                group: 'PERIPHERAL',
                refId: 'gpio',
                default: '',
            },
            {
                id: 'D2',
                name: 'D2',
                settingType: SettingType.OPTION,
                required: true,
                group: 'PERIPHERAL',
                refId: 'gpio',
                default: '',
            },
            {
                id: 'D3',
                name: 'D3',
                settingType: SettingType.OPTION,
                required: true,
                group: 'PERIPHERAL',
                refId: 'gpio',
                default: '',
            },
            {
                id: 'D4',
                name: 'D4',
                settingType: SettingType.OPTION,
                required: true,
                group: 'PERIPHERAL',
                refId: 'gpio',
                default: '',
            },
            {
                id: 'D5',
                name: 'D5',
                settingType: SettingType.OPTION,
                required: true,
                group: 'PERIPHERAL',
                refId: 'gpio',
                default: '',
            },
            {
                id: 'D6',
                name: 'D6',
                settingType: SettingType.OPTION,
                required: true,
                group: 'PERIPHERAL',
                refId: 'gpio',
                default: '',
            },
            {
                id: 'D7',
                name: 'D7',
                settingType: SettingType.OPTION,
                required: true,
                group: 'PERIPHERAL',
                refId: 'gpio',
                default: '',
            },
            {
                id: 'RESETGPIOS',
                name: 'Reset GPIOs',
                settingType: SettingType.OPTION,
                required: true,
                group: 'PERIPHERAL',
                refId: 'gpio',
                default: '',
            },
            {
                id: 'DCGPIOS',
                name: 'DC GPIOs',
                settingType: SettingType.OPTION,
                required: true,
                group: 'PERIPHERAL',
                refId: 'gpio',
                default: [],
            },
            {
                id: 'WRGPIOS',
                name: 'WR GPIOs',
                settingType: SettingType.OPTION,
                required: true,
                group: 'PERIPHERAL',
                refId: 'gpio',
                default: [],
            },
            {
                id: 'RDGPIOS',
                name: 'RD GPIOs',
                settingType: SettingType.OPTION,
                required: true,
                group: 'PERIPHERAL',
                refId: 'gpio',
                default: [],
            },
            {
                id: 'XOFFSET',
                name: 'X Offset Address',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'ADVANCED',
                default: 0,
            },
            {
                id: 'YOFFSET',
                name: 'Y Offset Address',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'ADVANCED',
                default: 0,
            },
            {
                id: 'VCOM',
                name: 'VCOM Setting',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'ADVANCED',
                default: '0x19',
            },
            {
                id: 'GCTRL',
                name: 'Gate Control',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'ADVANCED',
                default: '0x35',
            },
            {
                id: 'VRHS',
                name: 'VRH Setting',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'ADVANCED',
                default: '0x12',
            },
            {
                id: 'VDVS',
                name: 'VDV Setting',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'ADVANCED',
                default: '0x20',
            },
            {
                id: 'MDAC',
                name: 'Memory Data Access Control',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'ADVANCED',
                default: '0x00',
            },
            {
                id: 'GAMMA',
                name: 'Gamma',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'ADVANCED',
                default: '0x01',
            },
            {
                id: 'COLMOD',
                name: 'Interface Pixel Format',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'ADVANCED',
                default: '0x55',
            },
            {
                id: 'LCM',
                name: 'LCM Setting',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'ADVANCED',
                default: '0x2C',
            },
            {
                id: 'PORCH_PARAM',
                name: 'Porch Setting',
                settingType: SettingType.STRING,
                required: true,
                group: 'ADVANCED',
                default: '0c 0c 00 33 33',
            },
            {
                id: 'CMD2EN_PARAM',
                name: 'Command 2 Enable Parameter',
                settingType: SettingType.STRING,
                required: true,
                group: 'ADVANCED',
                default: '5a 69 02 00',
            },
            {
                id: 'PWCTRL1_PARAM',
                name: 'Command 2 Enable Parameter',
                settingType: SettingType.STRING,
                required: true,
                group: 'ADVANCED',
                default: 'a4 a1',
            },
            {
                id: 'PVGAM_PARAM',
                name: 'Positive Voltage Gamma Control Parameter',
                settingType: SettingType.STRING,
                required: true,
                group: 'ADVANCED',
                default: 'd0 00 02 07 0a 28 32 44 42 06 0e 12 14 17',
            },
            {
                id: 'NVGAM_PARAM',
                name: 'Negative Voltage Gamma Control Parameter',
                settingType: SettingType.STRING,
                required: true,
                group: 'ADVANCED',
                default: 'd0 00 02 07 0a 28 31 54 47 0e 1c 17 1b 1e',
            },
            {
                id: 'RAM_PARAM',
                name: 'RAM Control Parameter',
                settingType: SettingType.STRING,
                required: true,
                group: 'ADVANCED',
                default: '00 F0',
            },
            {
                id: 'RGB_PARAM',
                name: 'RGB Interface Control Parameter',
                settingType: SettingType.STRING,
                required: true,
                group: 'ADVANCED',
                default: '40 02 14',
            },
            {
                id: 'MIPI_MODE',
                name: 'MIPI Mode',
                settingType: SettingType.OPTION,
                required: true,
                group: 'ADVANCED',
                default: 'MIPI_DBI_MODE_SPI_4WIRE',
                options: [
                    {
                        name: 'MIPI_DBI_MODE_SPI_3WIRE',
                        value: 'MIPI_DBI_MODE_SPI_3WIRE',
                    },
                    {
                        name: 'MIPI_DBI_MODE_SPI_4WIRE',
                        value: 'MIPI_DBI_MODE_SPI_4WIRE',
                    },
                    {
                        name: 'MIPI_DBI_MODE_6800_BUS_16_BIT',
                        value: 'MIPI_DBI_MODE_6800_BUS_16_BIT',
                    },
                    {
                        name: 'MIPI_DBI_MODE_6800_BUS_9_BIT',
                        value: 'MIPI_DBI_MODE_6800_BUS_9_BIT',
                    },
                    {
                        name: 'MIPI_DBI_MODE_6800_BUS_8_BIT',
                        value: 'MIPI_DBI_MODE_6800_BUS_8_BIT',
                    },
                    {
                        name: 'MIPI_DBI_MODE_8080_BUS_16_BIT',
                        value: 'MIPI_DBI_MODE_8080_BUS_16_BIT',
                    },
                    {
                        name: 'MIPI_DBI_MODE_8080_BUS_9_BIT',
                        value: 'MIPI_DBI_MODE_8080_BUS_9_BIT',
                    },
                    {
                        name: 'MIPI_DBI_MODE_8080_BUS_8_BIT',
                        value: 'MIPI_DBI_MODE_8080_BUS_8_BIT',
                    },
                ],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getState(settingId: string): SettingState {
        const MODE = this.getSettingValue('MODE');
        switch (settingId) {
            case 'SPI':
                return MODE !== 'spi' ? SettingState.HIDDEN : SettingState.ENABLED;
            case 'DATAGPIOS':
                return MODE !== 'parallel' ? SettingState.HIDDEN : SettingState.ENABLED;
            default:
                return super.getState(settingId);
        }
    }

    getFiles(): Array<string> {
        if (this.project.runtime === 'micropython') {
            return micropython.getFiles(this);
        }
        return super.getFiles();
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            default:
                return [];
        }
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/taiko/ssi_i2c';
    }
}
